import { UseMutationResult, useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import debounce from 'lodash/debounce';
import { AxiosError } from 'axios';
import { setApiError } from '../store/slices/apiError.slice';
import { ErrorSource } from '../constant/model/api/api';
import { handleApiError } from '../constant/utils/error.helper';

export type MutationContext = {
  abortController: AbortController;
  requestId: string;
};

export interface CreateMutationOptions<TData, TVariables> {
  mutationFn: (variables: TVariables, signal: AbortSignal) => Promise<TData>;
  errorSource: ErrorSource;
  loadingActions: {
    start: (dispatch: any) => void;
    end: (dispatch: any) => void;
  };
  onSuccessHandler?: (data: TData, variables: TVariables, context: MutationContext | undefined, dispatch: any) => void;
  onErrorHandler?: (error: AxiosError | Error, variables: TVariables, context: MutationContext | undefined, dispatch: any) => void;
  onSettledHandler?: (data: TData | undefined, error: AxiosError | Error | null, variables: TVariables, context: MutationContext | undefined, dispatch: any) => void;
  retryHandler?: (failureCount: number, error: Error) => boolean;
  module?: string;
  debounceTime?: number;
}

export function createApiMutation<TData, TVariables>(
  options: CreateMutationOptions<TData, TVariables>
): UseMutationResult<TData, AxiosError | Error, TVariables, MutationContext> {
  const dispatch = useDispatch();
  const { 
    mutationFn, 
    errorSource, 
    loadingActions, 
    onSuccessHandler,
    onErrorHandler,
    onSettledHandler,
    retryHandler, 
    module = 'API',
    debounceTime = 300
  } = options;

  const mutation = useMutation<TData, AxiosError | Error, TVariables, MutationContext>(
    async (variables: TVariables) => {
      const abortController = new AbortController();
      const requestId = uuidv4();
      
      console.info(module, `Starting request ${requestId}`);
      
      try {
        const response = await mutationFn(variables, abortController.signal);
        console.info(module, `Request ${requestId} completed successfully`);
        return response;
      } catch (error) {
        console.error(module, `Request ${requestId} failed:`, error);
        throw error;
      }
    },
    {
      retry: (failureCount, error) => {
        if (retryHandler) {
          return retryHandler(failureCount, error);
        }

        if (error instanceof AxiosError && error.response?.status === 400) {
          return false;
        }

        if (error instanceof AxiosError && !error.response && failureCount < 2) {
          console.warn(module, `Network error, retrying (${failureCount + 1}/3)...`);
          return true;
        }
        
        if (error instanceof AxiosError && 
            error.response?.status && 
            error.response.status >= 500 && 
            failureCount < 1) {
          console.warn(module, `Server error ${error.response.status}, retrying...`);
          return true;
        }
        
        return false;
      },
      onMutate: () => {
        loadingActions.start(dispatch);
        
        return {
          abortController: new AbortController(),
          requestId: uuidv4()
        };
      },
    
      onSuccess: (data, variables, context) => {
        if (onSuccessHandler) {
          onSuccessHandler(data, variables, context, dispatch);
        }
        console.info(module, `Successfully processed request ${context?.requestId}`);
      },

      onError: (error, variables, context) => {
        const errorDetails = handleApiError(error);
        console.error(
          module, 
          `Error (RequestID: ${context?.requestId}):`, 
          errorDetails
        );
        
        dispatch(setApiError({
          source: errorSource,
          error: {
            message: errorDetails.message || 'Request failed',
            statusCode: errorDetails.status || 500,
            stack: errorDetails.stack || '',
            requestId: context?.requestId
          }
        }));

        if (onErrorHandler) {
          onErrorHandler(error, variables, context, dispatch);
        }
      },
      
      onSettled: (data, error, variables, context) => {
        loadingActions.end(dispatch);
        
        if (context?.abortController) {
          try {
            context.abortController.abort();
          } catch (e) {
            // Ignore abort errors
          }
        }
        
        console.info(module, `Request ${context?.requestId} settled`);

        if (onSettledHandler) {
          onSettledHandler(data, error, variables, context, dispatch);
        }
      }
    }
  );
  
  const debouncedMutate = useRef(
    debounce((variables: TVariables) => {
      console.info(module, 'Executing debounced request');
      mutation.mutate(variables);
    }, debounceTime)
  ).current;
  
  return {
    ...mutation,
    mutate: debouncedMutate,
    mutateAsync: mutation.mutateAsync
  } as UseMutationResult<TData, AxiosError | Error, TVariables, MutationContext>;
}